import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  BooksIcon, ClockCounterClockwiseIcon,
  ShieldStarIcon, SignOutIcon, KeyIcon, BriefcaseIcon, TrayIcon,
} from "@phosphor-icons/react";
import { ChangePasswordDialog } from "@/components/generator/ChangePasswordDialog";

export const Header = ({ onOpenHistory, onOpenKb, onOpenProjects, onOpenJobs }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cpOpen, setCpOpen] = useState(false);
  const [newJobs, setNewJobs] = useState(0);

  useEffect(() => {
    let alive = true;
    const fetchCount = () =>
      api.get("/jobs").then(({ data }) => { if (alive) setNewJobs(data.filter((j) => j.status === "new").length); }).catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <header
      data-testid="app-header"
      className="h-14 shrink-0 border-b border-zinc-800 flex items-center justify-between px-4 bg-zinc-950/80 backdrop-blur-xl z-50"
    >
      <div className="flex items-center gap-3">
        <img src="/soverenx-logo.jpg" alt="SoverenX Systems" className="h-8 object-contain" data-testid="header-logo" />
        <span className="hidden md:inline text-[10px] uppercase tracking-[0.2em] text-zinc-600">config engine</span>
      </div>

      <div className="flex items-center gap-3">
        <button data-testid="open-kb-btn" onClick={onOpenKb}
          className="flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition-colors duration-150">
          <BooksIcon className="h-4 w-4" /> <span className="hidden lg:inline">Knowledge Base</span>
        </button>
        <button data-testid="open-jobs-btn" onClick={onOpenJobs}
          className="relative flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition-colors duration-150">
          <TrayIcon className="h-4 w-4" /> <span className="hidden lg:inline">Jobs</span>
          {newJobs > 0 && (
            <span data-testid="jobs-badge"
              className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-cyan-500 px-1 text-[9px] font-bold text-zinc-950 ring-2 ring-zinc-950">
              {newJobs}
            </span>
          )}
        </button>
        <button data-testid="open-projects-btn" onClick={onOpenProjects}
          className="flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition-colors duration-150">
          <BriefcaseIcon className="h-4 w-4" /> <span className="hidden lg:inline">Projects</span>
        </button>
        <button data-testid="open-history-btn" onClick={onOpenHistory}
          className="flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition-colors duration-150">
          <ClockCounterClockwiseIcon className="h-4 w-4" /> <span className="hidden lg:inline">History</span>
        </button>
        {user?.role === "admin" && (
          <button data-testid="open-admin-btn" onClick={() => navigate("/admin")}
            className="flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-xs text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 transition-colors duration-150">
            <ShieldStarIcon className="h-4 w-4" /> <span className="hidden lg:inline">Admin</span>
          </button>
        )}

        <div className="h-5 w-px bg-zinc-800" />

        <span className="hidden sm:inline text-xs text-zinc-500 font-mono max-w-[160px] truncate" data-testid="header-user">{user?.email}</span>
        <button data-testid="change-password-btn" onClick={() => setCpOpen(true)} title="Change password"
          className="flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-xs text-zinc-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors duration-150">
          <KeyIcon className="h-4 w-4" />
        </button>
        <button data-testid="logout-btn" onClick={logout} title="Sign out"
          className="flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-xs text-zinc-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors duration-150">
          <SignOutIcon className="h-4 w-4" />
        </button>
      </div>
      <ChangePasswordDialog open={cpOpen} onOpenChange={setCpOpen} />
    </header>
  );
};
